<div class="billing-container" *ngIf="!loading; else loadingSpinner">
  <ng-container *ngIf="invoice">
    <!-- Toolbar (board: Back · "Invoice INV-…" · SMS · Email · Print). Hidden in print.
         Phone: lead row stays on top, actions become the sticky bottom bar. -->
    <div class="inv-toolbar no-print">
      <div class="inv-lead">
        <button type="button" class="inv-back" (click)="goBack()" aria-label="Back">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/></svg>
          <span class="inv-back-label">Back</span>
        </button>
        <div class="inv-title">
          <b>{{ invoice.invoiceNumber ? 'Invoice' : 'Provisional bill' }}</b>
          <span class="inv-title-no" *ngIf="invoice.invoiceNumber">{{ invoice.invoiceNumber }}</span>
        </div>
      </div>

      <!-- Action Buttons (hidden during print) -->
      <div class="action-buttons inv-actions no-print">
        <button type="button" class="inv-btn inv-btn--ghost" (click)="sendInvoice(true, false)">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
          Send via SMS
        </button>
        <button type="button" class="inv-btn inv-btn--ghost" (click)="sendInvoice(false, true)">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/></svg>
          Send via Email
        </button>
        <button type="button" class="inv-btn inv-btn--primary" (click)="printInvoice()">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9V3h12v6"/><rect x="3" y="9" width="18" height="8" rx="2"/><path d="M6 14h12v7H6z"/></svg>
          Print / Save as PDF
        </button>
      </div>
    </div>

    <article class="invoice-card">
      <!-- Hero: hospital + invoice meta, (phone) total, patient & visit -->
      <div class="inv-hero">
        <!-- Header -->
        <div class="invoice-header">
          <div class="inv-brand">
            <span class="inv-logo" aria-hidden="true">{{ hospitalInitials }}</span>
            <div class="inv-brand-text">
              <h1>{{ invoice.hospitalName }}</h1>
              <p class="inv-address">{{ invoice.hospitalAddress }}</p>
              <p class="inv-gstin" *ngIf="invoice.gstRegistrationNumber">GSTIN: {{ invoice.gstRegistrationNumber }}</p>
            </div>
          </div>
          <div class="invoice-number">
            <!-- The number is only given at checkout; before that this is not a tax invoice. -->
            <h2>{{ invoice.invoiceNumber ? 'Tax Invoice' : 'Provisional bill — not a tax invoice' }}</h2>
            <p class="inv-no" *ngIf="invoice.invoiceNumber">{{ invoice.invoiceNumber }}</p>
            <p class="inv-generated">Generated {{ invoice.invoiceGeneratedAt | ist: 'dd/MM/yyyy' }}</p>
            <!-- Status Badge -->
            <span class="inv-pill" [ngClass]="invoice.status.toLowerCase()">
              {{ invoice.status }}<ng-container *ngIf="invoice.paidAt"><span class="inv-pill-date"> · {{ invoice.paidAt | ist: 'dd MMM yyyy' }}</span></ng-container>
            </span>
          </div>
        </div>

        <!-- Phone-only hero total (board: big centred amount between dashed rules) -->
        <div class="inv-hero-total">
          <span class="inv-hero-label">Total amount</span>
          <b class="inv-hero-amt">₹{{ invoice.totalAmount | number: '1.2-2' }}</b>
          <span class="inv-hero-sub" *ngIf="invoice.paidAt">Paid · {{ invoice.paidAt | ist: 'dd MMM, hh:mm a' }}</span>
          <span class="inv-hero-sub" *ngIf="!invoice.paidAt">{{ invoice.status }}<ng-container *ngIf="invoice.balanceDue > 0"> · ₹{{ invoice.balanceDue | number: '1.2-2' }} due</ng-container></span>
        </div>

        <!-- Patient & Visit Info -->
        <div class="info-section">
          <div class="info-block">
            <h3>Patient Details</h3>
            <div class="inv-kv inv-kv--primary"><span class="inv-k">Name</span><b class="inv-v">{{ invoice.patientName }}</b></div>
            <div class="inv-kv"><span class="inv-k">Phone</span><span class="inv-v">{{ invoice.patientPhone }}</span></div>
            <div class="inv-kv" *ngIf="hasDateOfBirth"><span class="inv-k">DOB</span><span class="inv-v">{{ invoice.dateOfBirth | ist: 'dd/MM/yyyy' }}</span></div>
          </div>
          <div class="info-block">
            <h3>Visit Details</h3>
            <div class="inv-kv inv-kv--primary"><span class="inv-k">Department</span><b class="inv-v">{{ invoice.department }}</b></div>
            <div class="inv-kv"><span class="inv-k">Check-in</span><span class="inv-v">{{ invoice.checkInTime | ist: 'dd/MM/yyyy HH:mm' }}</span></div>
            <div class="inv-kv"><span class="inv-k">Check-out</span><span class="inv-v">{{ invoice.checkOutTime | ist: 'dd/MM/yyyy HH:mm' }}</span></div>
          </div>
        </div>
      </div>

      <!-- Items: line items + totals (phone: one white card) -->
      <div class="inv-items">
        <!-- Line Items Table -->
        <table class="items-table">
          <thead>
            <tr>
              <th class="col-no">#</th>
              <th class="col-desc">Description</th>
              <th class="text-right">Qty</th>
              <th class="text-right">Amount</th>
              <th class="text-right">Tax</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let item of invoice.lineItems; let idx = index">
              <td class="col-no">{{ idx + 1 }}</td>
              <td class="col-desc">
                <b class="inv-item-name">{{ item.description }}</b>
                <span class="inv-item-sub" *ngIf="item.category || item.notes">{{ categoryLabel(item.category) }}<ng-container *ngIf="item.category && item.notes"> · </ng-container>{{ item.notes }}</span>
              </td>
              <td class="text-right col-qty">{{ item.quantity }}</td>
              <td class="text-right col-amt">₹{{ item.amount * item.quantity | number: '1.2-2' }}</td>
              <td class="text-right col-tax">₹{{ item.taxAmount | number: '1.2-2' }}</td>
            </tr>
          </tbody>
        </table>

        <!-- Payment note + Totals -->
        <div class="inv-bottom">
          <div class="inv-pay-note">
            <b>Payment</b>
            <span *ngIf="invoice.paidAt">Paid on: {{ invoice.paidAt | ist: 'dd/MM/yyyy HH:mm' }}</span>
            <span *ngIf="!invoice.paidAt">Status: {{ invoice.status }}</span>
          </div>
          <div class="totals-section">
            <div class="total-row">
              <span>Subtotal</span>
              <span>₹{{ invoice.subTotal | number: '1.2-2' }}</span>
            </div>
            <div class="total-row">
              <!-- Each line carries its own GST rate, so the bill's single percentage can read 0 with tax on it. -->
              <span>GST<ng-container *ngIf="invoice.taxPercentage > 0"> ({{ invoice.taxPercentage }}%)</ng-container></span>
              <span>₹{{ invoice.taxAmount | number: '1.2-2' }}</span>
            </div>
            <div class="total-row grand-total">
              <span>TOTAL AMOUNT</span>
              <span>₹{{ invoice.totalAmount | number: '1.2-2' }}</span>
            </div>
            <div class="total-row balance-due" *ngIf="invoice.balanceDue > 0">
              <span>BALANCE DUE</span>
              <span>₹{{ invoice.balanceDue | number: '1.2-2' }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="inv-footer">
        <span>This is a computer-generated invoice and does not need a signature.<ng-container *ngIf="invoice.hospitalPhone"><br>Queries: {{ invoice.hospitalPhone }}</ng-container></span>
      </div>
    </article>
  </ng-container>

  <div class="error-message" *ngIf="error">
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/></svg>
    <span class="error-text">{{ error }}</span>
    <button type="button" class="inv-btn inv-btn--ghost" (click)="goBack()">Back</button>
  </div>

  <!-- Nothing loaded and no error (e.g. missing/invalid bill id) — never a blank desk -->
  <div class="inv-empty" *ngIf="!invoice && !error">
    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"/><path d="M14 3v6h6"/></svg>
    <span class="inv-empty-text">No invoice to show.</span>
    <button type="button" class="inv-btn inv-btn--ghost" (click)="goBack()">Back</button>
  </div>
</div>

<ng-template #loadingSpinner>
  <div class="loading">
    <span class="loading-ring" aria-hidden="true"></span>
    <p>Loading invoice...</p>
  </div>
</ng-template>
